import React, { useState } from 'react';
import { Btn, Msg, Section, panelPost } from '../kit';
import { Labeled, inputCls } from '../email/emailKit';
import { TrustedBy as HomeTrustedBy } from '../../../landing-page/components/NewHome';
import type { HomepageContent } from '../../../services/apiService';
import {
  LogoSlot, type Note, PreviewFrame, Radio, S, SaveBar, type TabProps, Toggle, type TrustedItem, type TrustedSetting,
  logoKind, move, nextKey, useDraft,
} from './shared';

/**
 * Trusted by — the "Trusted by R&D leaders at" logos directly above Success
 * Stories: title, subtitle, layout, the logos (name + image, shown/hidden,
 * order) and the closing "...more!". Layouts: cards (a grid of logo cards,
 * the default since 2026-10-14), or a banner in two rows or scrolling — only
 * the banner ends with the closing text. POST homepage/save_trusted replaces
 * the whole list (order = display order); a picked file goes to
 * homepage/upload_logo first and the row takes the returned url. The public
 * /homepage/content is cached for 30 s, so a save shows within half a minute.
 */

type Layout = HomepageContent['trusted']['layout'];
type ItemDraft = TrustedItem & { key: string };
type Draft = {
  enabled: boolean; title: string; subtitle: string; layout: Layout; more_text: string; items: ItemDraft[];
};

/** homepage_content.LAYOUT_LABELS, for a backend that does not send them. */
const LAYOUTS: Record<Layout, string> = { cards: 'Cards (grid)', rows: 'Two rows', scroll: 'Scrolling' };
const asLayout = (v: unknown): Layout => (v === 'rows' || v === 'scroll' ? v : 'cards');

const draftFrom = (t: TrustedSetting): Draft => ({
  enabled: t?.enabled !== false,
  title: t?.title || '',
  subtitle: typeof t?.subtitle === 'string' ? t.subtitle : '',
  layout: asLayout(t?.layout),
  more_text: t?.more_text || '',
  items: (Array.isArray(t?.items) ? t.items : []).map(it => ({
    id: String(it.id || ''), name: String(it.name || ''), logo: String(it.logo || ''),
    enabled: it.enabled !== false, key: nextKey(),
  })),
});

/** homepage_content.ASSET_MIMES, by extension for the files a browser types as ''. */
const MIME_BY_EXT: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml',
};
const MIMES = new Set(Object.values(MIME_BY_EXT));
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.png,.jpg,.jpeg,.webp,.gif,.svg';

const readDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result || ''));
  fr.onerror = () => reject(fr.error || new Error('Could not read the file'));
  fr.readAsDataURL(file);
});

const newId = () => Math.random().toString(16).slice(2, 10);

/** The section as the homepage draws it — the homepage's own component, fed
 *  the unsaved draft (shown even while switched off, dimmed). The columns
 *  follow this window's width, as they do on the site. */
const BannerPreview: React.FC<{ d: Draft }> = ({ d }) => {
  const items = d.items.filter(it => it.enabled && it.name.trim());
  const off = !d.enabled || !items.length;
  const note = !d.enabled ? 'switched off — the homepage does not show it'
    : !items.length ? 'no logos are shown — the homepage hides the section' : undefined;
  const content: HomepageContent['trusted'] = {
    enabled: true, title: d.title, subtitle: d.subtitle, layout: d.layout, more_text: d.more_text,
    items: items.map(it => ({ id: it.id, name: it.name.trim(), logo: it.logo })),
  };
  return (
    <PreviewFrame note={note} dim={off}>
      {items.length > 0 && <HomeTrustedBy content={content} bare />}
    </PreviewFrame>
  );
};

const TrustedBy: React.FC<TabProps> = ({ data, onSaved, onDirty }) => {
  const { draft, edit, dirty, discard, settle } = useDraft(data.settings.trusted, draftFrom, onDirty);
  const [busy, setBusy] = useState<'' | 'save' | 'reset'>('');
  const [msg, setMsg] = useState<Note>(null);
  const [uploading, setUploading] = useState('');
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const maxItems = data.limits?.items || 24;
  const assetKb = data.limits?.asset_kb || 600;
  const subtitleMax = data.limits?.subtitle || 200;
  // The server's labels where it sends them, in this order: cards first.
  const layouts = (Object.keys(LAYOUTS) as Layout[]).map(k => ({ k, label: data.layouts?.[k] || LAYOUTS[k] }));

  const setField = (patch: Partial<Draft>) => edit(d => ({ ...d, ...patch }));
  const setItem = (key: string, patch: Partial<ItemDraft>) =>
    edit(d => ({ ...d, items: d.items.map(it => (it.key === key ? { ...it, ...patch } : it)) }));
  const moveItem = (i: number, dir: -1 | 1) => edit(d => ({ ...d, items: move(d.items, i, dir) }));
  const removeItem = (key: string) => edit(d => ({ ...d, items: d.items.filter(it => it.key !== key) }));
  const addItem = () => edit(d => ({
    ...d, items: [...d.items, { id: newId(), name: '', logo: '', enabled: true, key: nextKey() }],
  }));
  const setErr = (key: string, text: string) => setRowErr(e => ({ ...e, [key]: text }));

  const upload = async (key: string, file: File) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const mime = MIMES.has(file.type) ? file.type : (MIME_BY_EXT[ext] || '');
    if (!MIMES.has(mime)) { setErr(key, 'Upload a PNG, JPEG, WebP, GIF or SVG image.'); return; }
    if (file.size > assetKb * 1024) {
      setErr(key, `That file is ${Math.ceil(file.size / 1024).toLocaleString()} KB — logos can be at most ${assetKb} KB.`);
      return;
    }
    setUploading(key); setErr(key, '');
    let dataUrl = '';
    try { dataUrl = await readDataUrl(file); } catch {
      setUploading(''); setErr(key, 'The file could not be read.'); return;
    }
    const { data: r, error } = await panelPost(S, 'upload_logo', { filename: file.name, mime, data: dataUrl });
    setUploading('');
    if (error || !r?.url) { setErr(key, error || 'Upload failed'); return; }
    setItem(key, { logo: String(r.url) });
    setMsg({ kind: 'ok', text: `Uploaded ${file.name}. Save to put it on the homepage.` });
  };

  const save = async () => {
    if (!draft.title.trim()) { setMsg({ kind: 'error', text: 'The banner needs a title.' }); return; }
    if (draft.items.length > maxItems) { setMsg({ kind: 'error', text: `At most ${maxItems} logos.` }); return; }
    const unnamed = draft.items.findIndex(it => !it.name.trim());
    if (unnamed >= 0) { setMsg({ kind: 'error', text: `Logo ${unnamed + 1} needs a name.` }); return; }
    setBusy('save'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'save_trusted', {
      enabled: draft.enabled, title: draft.title.trim(), subtitle: draft.subtitle.trim(), layout: draft.layout,
      more_text: draft.more_text.trim(),
      items: draft.items.map(({ key: _k, ...it }) => ({ ...it, name: it.name.trim() })),
    });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Save failed' }); return; }
    onSaved(next);
    settle();
    setRowErr({});
    setMsg({ kind: 'ok', text: 'Saved — the homepage picks it up within half a minute.' });
  };

  const reset = async () => {
    setBusy('reset'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'reset', { section: 'trusted' });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Reset failed' }); return; }
    onSaved(next);
    settle();
    setRowErr({});
    setMsg({ kind: 'ok', text: 'Back to the built-in cards and their eight logos.' });
  };

  // What the homepage shows now, from the saved settings.
  const saved = data.settings.trusted;
  const savedShown = (saved?.items || []).filter(it => it.enabled !== false).length;
  const savedLayout = asLayout(saved?.layout);
  const nowLine = !saved?.enabled ? 'the section is switched off, so the homepage does not show it.'
    : !savedShown ? 'no logo is switched on, so the homepage hides the section.'
      : `“${saved.title}” with ${savedShown} logo${savedShown === 1 ? '' : 's'} `
        + `${savedLayout === 'cards' ? 'as cards' : savedLayout === 'scroll' ? 'scrolling' : 'in two rows'}`
        + `${savedLayout !== 'cards' && saved.more_text ? `, ending with “${saved.more_text}”` : ''}.`;
  const shownCount = draft.items.filter(it => it.enabled).length;
  const uploadCls = 'py-1 px-2.5 text-xs rounded-full font-grotesk font-medium whitespace-nowrap border '
    + 'border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 cursor-pointer';

  return (
    <Section title="Trusted by">
      <p className="text-xs text-grayscale-1100 -mt-1">
        The logos directly above Success Stories. Logos keep their own colours and sit in equal slots,
        never stretched; a logo without an image (or one that fails to load) shows its name as text.
      </p>
      <div className="text-xs text-grayscale-1200">On the homepage now: {nowLine}</div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <div className="flex items-center gap-2">
        <Toggle on={draft.enabled} onChange={v => setField({ enabled: v })} label="Show the section" />
        <span className="text-sm text-grayscale-1200">Show the section</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Labeled label="Title">
          <input value={draft.title} maxLength={120} onChange={e => setField({ title: e.target.value })}
            placeholder="Trusted by R&D leaders at" className={inputCls} />
        </Labeled>
        <Labeled label="Subtitle" hint="under the title · leave empty to hide it">
          <input value={draft.subtitle} maxLength={subtitleMax} onChange={e => setField({ subtitle: e.target.value })}
            placeholder="Industry, investors and research institutions already discovering tech on Cosmos"
            className={inputCls} />
        </Labeled>
      </div>
      <div className="grid gap-3 md:grid-cols-2 items-start">
        <div role="radiogroup" aria-label="Layout" className="flex flex-col gap-1 text-xs text-grayscale-1100 min-w-0">
          <span>Layout</span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 min-h-9">
            {layouts.map(({ k, label }) => (
              <Radio key={k} name="trusted-layout" checked={draft.layout === k} onChange={() => setField({ layout: k })}>
                {label}
              </Radio>
            ))}
          </div>
        </div>
        <Labeled label="Closing text"
          hint={draft.layout === 'cards' ? 'not shown with cards' : 'after the last logo'}>
          <input value={draft.more_text} maxLength={40} onChange={e => setField({ more_text: e.target.value })}
            placeholder="...more!" className={`${inputCls} ${draft.layout === 'cards' ? 'opacity-60' : ''}`} />
        </Labeled>
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-xs text-grayscale-1100">
          Logos — {shownCount} shown{draft.items.length > shownCount ? `, ${draft.items.length - shownCount} hidden` : ''}
          {' '}· in this order · at most {maxItems} · images up to {assetKb} KB (PNG, JPEG, WebP, GIF or SVG)
        </div>
        {draft.items.map((it, i) => (
          <div key={it.key}
            className={`flex items-center gap-2 flex-wrap rounded-lg border border-grayscale-300 p-2 ${it.enabled ? '' : 'bg-grayscale-200'}`}>
            <span className="text-xs text-grayscale-1100 w-5 text-right">{i + 1}</span>
            <LogoSlot name={it.name} logo={it.logo}
              className={`h-10 w-28 shrink-0 rounded border border-grayscale-300 bg-white px-1 ${it.enabled ? '' : 'opacity-50'}`} />
            <div className="flex flex-col gap-0.5 flex-1 min-w-[12rem]">
              <input value={it.name} maxLength={120} aria-label={`Logo ${i + 1} name`}
                onChange={e => setItem(it.key, { name: e.target.value })}
                placeholder="Name — the alt text, and what shows without an image" className={inputCls} />
              <span className="text-[11px] text-grayscale-1100">
                {uploading === it.key ? 'Uploading…' : logoKind(it.logo)}
              </span>
            </div>
            <label className={`${uploadCls} ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
              Upload logo
              <input type="file" accept={ACCEPT} className="hidden" disabled={!!uploading}
                onChange={e => {
                  const f = e.target.files?.[0];
                  e.target.value = '';   // the same file can be picked again
                  if (f) upload(it.key, f);
                }} />
            </label>
            {it.logo && <Btn small onClick={() => setItem(it.key, { logo: '' })}
              title="Show the name as text instead">Remove logo</Btn>}
            <span className="flex items-center gap-1.5">
              <Toggle on={it.enabled} onChange={v => setItem(it.key, { enabled: v })}
                label={`${it.name || `Logo ${i + 1}`}: shown on the homepage`} />
              <span className="text-xs text-grayscale-1200 w-12">{it.enabled ? 'Shown' : 'Hidden'}</span>
            </span>
            <Btn small onClick={() => moveItem(i, -1)} disabled={i === 0} title="Move up">↑</Btn>
            <Btn small onClick={() => moveItem(i, 1)} disabled={i === draft.items.length - 1} title="Move down">↓</Btn>
            <Btn small kind="danger" onClick={() => removeItem(it.key)} title="Delete this logo (Discard brings it back)">
              Delete
            </Btn>
            {rowErr[it.key] && <div className="basis-full text-xs text-red-600 pl-7">{rowErr[it.key]}</div>}
          </div>
        ))}
        {!draft.items.length && (
          <div className="text-sm text-grayscale-1100 py-3 text-center">No logos — the homepage hides the section.</div>
        )}
        <div>
          <Btn small onClick={addItem} disabled={draft.items.length >= maxItems}
            title={draft.items.length >= maxItems ? `At most ${maxItems} logos` : undefined}>
            + Add logo
          </Btn>
        </div>
      </div>

      <BannerPreview d={draft} />

      <SaveBar dirty={dirty} busy={busy} onSave={save} onDiscard={() => { discard(); setRowErr({}); setMsg(null); }}
        onReset={reset} />
    </Section>
  );
};

export default TrustedBy;
